import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Image,
  Platform,
  StyleSheet,
  Text,
  TextInput as RNTextInput,
  View,
} from 'react-native';

import {
  useInitiateResetPhoneNumberPublicMutation,
  useConfirmResetPhoneNumberPublicMutation,
  useDoResetPhoneNumberPublicMutation,
} from 'apollo/queries/autogenerate/hooks-public';
import { PinCodeDispatchMethod } from 'apollo/queries/autogenerate/schemas';
import { Platform as ReCaptchaPlatform } from 'apollo/queries/autogenerate/schemas-public';
import IMAGES from 'shared/constants/images';
import Button from 'shared/modules/common/components/atoms/Button';
import CodeInput from 'shared/modules/common/components/atoms/CodeInput';
import Dialog from 'shared/modules/common/components/atoms/Dialog';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import OTPTimer from 'shared/modules/common/components/organisms/OTPTimer';
import { getParsedPhoneNumber } from 'shared/utils/phone';
import validator, { rules } from 'shared/utils/validator';

const PHONE_NUMBER_ENTRY_REGEXP = /[^0-9]/;

type FormProps = {
  email: string;
  currentPhoneNumber: string;
  newPhoneNumber: string;
  emailOTP: string;
};

type Props = {
  isVisible: boolean;
  onClose: () => void;
  /**
   * Invoked when editing is successful.
   * Automatically invoked in 3 seconds after successful update.
   */
  onSuccess?: () => void;
  /**
   * WEB | IOS | ANDROID
   */
  reCaptchaPlatform: ReCaptchaPlatform;
  /**
   *  Every step requires a new ReCaptcha token. This function should return a Promise with the ReCaptcha token as the resolved value.
   */
  getRecaptchaTokenFunction: () => Promise<string>;
  onNavigateBack: () => void;
};

const EditUserPhoneUnauthenticated = (props: Props) => {
  const [stepCounter, setStepCounter] = useState(1);

  const [
    initiateResetPhoneNumberPublic,
  ] = useInitiateResetPhoneNumberPublicMutation({
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });
  const [
    confirmResetPhoneNumberPublic,
  ] = useConfirmResetPhoneNumberPublicMutation({
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });
  const [doResetPhoneNumberPublic] = useDoResetPhoneNumberPublicMutation({
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });

  const [errorMessage, setErrorMessage] = useState<string | null>('');
  const [samePhoneErrorMessage, setSamePhoneErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const form = useFormWithValidations({
    email: '',
    currentPhoneNumber: '',
    newPhoneNumber: '',
    emailOTP: '',
  });
  const codeInputRef = useRef<RNTextInput>(null);

  useEffect(() => codeInputRef.current?.focus(), [stepCounter]);

  const validateEmailAndPhone = async () => {
    const { currentPhoneNumber, newPhoneNumber } = form.getValues();

    const { isError, message } = rules.mustNotBeSame(
      getParsedPhoneNumber(currentPhoneNumber),
      getParsedPhoneNumber(newPhoneNumber),
      '携帯電話番号'
    );

    if (isError) {
      setSamePhoneErrorMessage(message);
    } else {
      setSamePhoneErrorMessage('');
    }

    await form.triggerValidation([
      'email',
      'currentPhoneNumber',
      'newPhoneNumber',
    ]);
  };

  // Sends an OTP code to user's email.
  // Can be invoked to request a new email OTP.
  const initiateResetPhoneNumber = useCallback(
    throttle(
      async () => {
        try {
          const { email, currentPhoneNumber } = form.getValues();

          const reCaptchaToken = await props.getRecaptchaTokenFunction();

          setShowError(false);
          await initiateResetPhoneNumberPublic({
            variables: {
              email,
              currentPhoneNumber: getParsedPhoneNumber(currentPhoneNumber),
              recaptchaPlatform: props.reCaptchaPlatform,
              recaptchaToken: reCaptchaToken,
            },
          });

          setStepCounter(2);
        } catch (err) {
          setErrorMessage('入力内容を確認してください');
        }
      },
      2000,
      { leading: true, trailing: false }
    ),
    [stepCounter]
  );

  // Sends an OTP code to the user's new phone number.
  // Can be invoked to request a new phone number OTP.
  const confirmResetPhoneNumber = async () => {
    try {
      const {
        email,
        currentPhoneNumber,
        newPhoneNumber,
        emailOTP,
      } = form.getValues();

      const reCaptchaToken = await props.getRecaptchaTokenFunction();

      setShowError(false);
      await confirmResetPhoneNumberPublic({
        variables: {
          email,
          currentPhoneNumber: getParsedPhoneNumber(currentPhoneNumber),
          newPhoneNumber: getParsedPhoneNumber(newPhoneNumber),
          otp: emailOTP,
          pinCodeDispatchMethod:
            stepCounter === 5
              ? PinCodeDispatchMethod.VOICE
              : PinCodeDispatchMethod.SMS,
          recaptchaPlatform: props.reCaptchaPlatform,
          recaptchaToken: reCaptchaToken,
        },
      });

      setStepCounter(4);
    } catch (err: any) {
      // Handle bad request (400) - send the user back to the beginning
      if (err.graphQLErrors?.[0]?.extensions?.code === 400) {
        setErrorMessage('入力内容を確認してください');
        setStepCounter(1);

        return;
      }

      setShowError(true);
    }
  };

  // If the correct phone number OTP is submitted, this permanently updates the user's phone number.
  // Cannot be invoked to request a new phone number OTP.
  const doResetPhoneNumber = async (code: string) => {
    try {
      const { email, currentPhoneNumber } = form.getValues();

      const reCaptchaToken = await props.getRecaptchaTokenFunction();

      setShowError(false);
      await doResetPhoneNumberPublic({
        variables: {
          email,
          currentPhoneNumber: getParsedPhoneNumber(currentPhoneNumber),
          otp: code,
          recaptchaPlatform: props.reCaptchaPlatform,
          recaptchaToken: reCaptchaToken,
        },
      });

      setStepCounter(6);
      setTimeout(() => {
        cleanUpState();
        props.onSuccess?.();
      }, 3000);
    } catch (err) {
      setShowError(true);
    }
  };

  const checkValidities = () => {
    const { currentPhoneNumber, newPhoneNumber, email } = form.getValues();

    if (!email || !currentPhoneNumber || !newPhoneNumber) {
      return true;
    }

    if (
      rules.mustBeValidEmail(email) !== true ||
      rules.mustBeValidPhone(currentPhoneNumber) !== true ||
      rules.mustBeValidPhone(newPhoneNumber) !== true
    ) {
      return true;
    }

    const { isError } = rules.mustNotBeSame(
      getParsedPhoneNumber(currentPhoneNumber),
      getParsedPhoneNumber(newPhoneNumber),
      '携帯電話番号'
    );

    if (isError) {
      return true;
    }

    if (form.errors.newPhoneNumber?.message) {
      setErrorMessage('');
      setSamePhoneErrorMessage('');
      form.clearError('newPhoneNumber');
      form.triggerValidation('newPhoneNumber');
    }

    return false;
  };

  const cleanUpState = () => {
    setStepCounter(1);
    setErrorMessage('');
    form.setValue('email', '');
    form.setValue('currentPhoneNumber', '');
    form.setValue('newPhoneNumber', '');
    form.clearError();
  };

  const onClose = () => {
    cleanUpState();
    props.onClose();
  };

  const onNavigateBack = () => {
    cleanUpState();
    props.onNavigateBack();
  };

  const onBackButtonPress = () => {
    if (stepCounter === 1) {
      onNavigateBack();
    } else {
      setErrorMessage(null);
      setStepCounter(stepCounter - 1);
    }
  };

  return (
    <Dialog
      title="携帯電話番号の確認"
      open={props.isVisible}
      style={style.dialog}
      showBackButton={stepCounter <= 2}
      onBackButtonPress={onBackButtonPress}
      onCloseButtonPress={onClose}
    >
      <View
        {...Platform.select({ web: { accessibilityRole: 'form' } })}
        style={style.container}
      >
        {/* STEP 1 - Current Email + Current Phone + New Phone */}
        {stepCounter === 1 && (
          <>
            <View style={{ flexGrow: 1 }}>
              <Text>
                現在ご利用中のメールアドレス・携帯電話番号と、新しい携帯電話番号を入力してください。
              </Text>
              <View style={style.spacer} />
              <TextInput
                label="現在のメールアドレス"
                placeholder="hello@paidy.com"
                value={form.getValues('email')}
                onChangeText={(text) => {
                  form.setValue('email', text);
                  validateEmailAndPhone();
                }}
                onBlur={() => {
                  setErrorMessage('');
                  form.clearError('email');
                  form.triggerValidation('email');
                  validateEmailAndPhone();
                }}
                message={
                  (form.errors.email?.message as string) || errorMessage || ''
                }
                state={
                  form.errors.email?.message || errorMessage
                    ? 'error'
                    : undefined
                }
                id="edit-user-phone-current-email-field"
              />
              <View style={style.spacer} />
            </View>
            <View style={{ flexGrow: 1 }}>
              <View style={style.spacer} />
              <TextInput
                placeholder="09000000000"
                label="現在の携帯電話番号"
                keyboardType="phone-pad"
                textContentType="telephoneNumber"
                onChangeText={(text) => {
                  // Allow only digits
                  if (text.match(PHONE_NUMBER_ENTRY_REGEXP) !== null) {
                    return;
                  }

                  form.setValue('currentPhoneNumber', text);
                  validateEmailAndPhone();
                }}
                value={form.getValues('currentPhoneNumber')}
                onBlur={() => {
                  setErrorMessage('');
                  form.clearError('currentPhoneNumber');
                  form.triggerValidation('currentPhoneNumber');
                  validateEmailAndPhone();
                }}
                message={
                  (form.errors.currentPhoneNumber?.message as string) ||
                  errorMessage ||
                  ''
                }
                state={
                  form.errors.currentPhoneNumber?.message || errorMessage
                    ? 'error'
                    : undefined
                }
                id="edit-user-phone-current-phone-field"
              />
              <View style={style.spacer} />
            </View>
            <View style={{ flexGrow: 1 }}>
              <View style={style.spacer} />
              <TextInput
                placeholder="09000000000"
                label="新しい携帯電話番号"
                keyboardType="phone-pad"
                textContentType="telephoneNumber"
                onChangeText={(text) => {
                  // Allow only digits
                  if (text.match(PHONE_NUMBER_ENTRY_REGEXP) !== null) {
                    return;
                  }

                  form.setValue('newPhoneNumber', text);
                  validateEmailAndPhone();
                }}
                value={form.getValues('newPhoneNumber')}
                onBlur={() => {
                  setErrorMessage('');
                  setSamePhoneErrorMessage('');
                  form.clearError('newPhoneNumber');
                  form.triggerValidation('newPhoneNumber');
                  validateEmailAndPhone();
                }}
                message={
                  (form.errors.newPhoneNumber?.message as string) ||
                  errorMessage ||
                  samePhoneErrorMessage ||
                  ''
                }
                state={
                  form.errors.newPhoneNumber?.message ||
                  errorMessage ||
                  samePhoneErrorMessage
                    ? 'error'
                    : undefined
                }
                id="edit-user-phone-new-phone-field"
              />
              <View style={style.spacer} />
            </View>
            <Button
              title="次へ"
              onPress={initiateResetPhoneNumber}
              style={style.button}
              id="edit-user-phone-next-button"
              disabled={checkValidities()}
            />
          </>
        )}
        {/* STEP 2 - Email OTP Sent Notice */}
        {stepCounter === 2 && (
          <View style={style.container}>
            <View style={{ flexGrow: 1, justifyContent: 'center' }}>
              <Text style={style.textTitle}>
                ご登録のメールアドレスに認証コードを送信しました
              </Text>
              <View style={style.spacer} />
              <Text>
                正しいメールアドレスが入力された場合、ご登録中のメールアドレスに認証コードを送信しました。メールをご確認ください。
              </Text>
              <View style={style.spacer} />
            </View>
            <Button
              title="次へ"
              onPress={() => setStepCounter(3)}
              style={style.button}
              id="edit-user-phone-next-button"
            />
          </View>
        )}
        {/* STEP 3 - Email OTP Entry */}
        {stepCounter === 3 && (
          <>
            <View style={{ flexGrow: 1 }}>
              <Text style={style.textTitle}>認証コード入力</Text>
              <View style={style.spacer} />
              <Text>
                {form.getValues('email')}
                に送信された4桁の認証コードを入力してください。
              </Text>
              <View style={style.spacer} />
              <View style={style.otp}>
                <CodeInput
                  onFulFill={(code) => {
                    form.setValue('emailOTP', code);
                    confirmResetPhoneNumber();
                  }}
                  style={style.codeField}
                  errorMessage="認証コードに誤りがあります"
                  showError={showError}
                  id="edit-user-phone-code-field"
                  ref={codeInputRef}
                />
              </View>
              <OTPTimer
                sentToLabel="メール"
                onPress={initiateResetPhoneNumber}
              />
            </View>
          </>
        )}
        {/* STEP 4 - New Phone OTP Sent Notification */}
        {stepCounter === 4 && (
          <>
            <View style={{ flexGrow: 1, justifyContent: 'center' }}>
              <Text style={style.textTitle}>
                新しい携帯電話番号に認証コードを送信しました
              </Text>
              <View style={style.spacer} />
              <Text>
                {form.getValues('newPhoneNumber')}
                に送信された4桁の認証コードを確認してください。
              </Text>
              <View style={style.spacer} />
            </View>
            <Button
              title="次へ"
              onPress={() => setStepCounter(5)}
              style={style.button}
              id="edit-user-phone-next-button"
            />
          </>
        )}
        {/* STEP 5 - New Phone OTP Entry */}
        {stepCounter === 5 && (
          <>
            <View style={{ flexGrow: 1 }}>
              <Text style={style.textTitle}>認証コード入力</Text>
              <View style={style.spacer} />
              <Text>
                {form.getValues('newPhoneNumber')}
                に送信された4桁の認証コードを入力してください。
              </Text>
              <View style={style.spacer} />
              <View style={style.otp}>
                <CodeInput
                  onFulFill={doResetPhoneNumber}
                  style={style.codeField}
                  errorMessage="認証コードに誤りがあります"
                  showError={showError}
                  id="edit-user-phone-code-field"
                  ref={codeInputRef}
                />
              </View>
              <OTPTimer
                sentToLabel="自動音声案内"
                onPress={confirmResetPhoneNumber}
              />
            </View>
          </>
        )}
        {/* STEP 6 - SUCCESS */}
        {stepCounter === 6 && (
          <>
            <View style={{ flexGrow: 1, justifyContent: 'center' }}>
              <View style={{ alignItems: 'center' }}>
                <Image
                  source={{ uri: IMAGES.success }}
                  style={{ width: '100%', aspectRatio: 1 }}
                />
              </View>
              <View style={style.spacer} />
            </View>
            <Text
              style={[style.textTitle, { fontSize: 28, textAlign: 'center' }]}
            >
              携帯電話番号の変更が完了しました。
            </Text>
          </>
        )}
      </View>
    </Dialog>
  );
};

const useFormWithValidations = (props: FormProps) => {
  const form = useForm<FormProps>({
    defaultValues: props,
  });

  useEffect(() => {
    form.register(
      {
        name: 'email',
      },
      {
        validate: validator.getRules(['mustBeValidEmail']),
      }
    );

    form.register(
      {
        name: 'currentPhoneNumber',
      },
      {
        validate: validator.getRules(['mustBeValidPhone']),
      }
    );

    form.register(
      {
        name: 'newPhoneNumber',
      },
      {
        validate: validator.getRules(['mustBeValidPhone']),
      }
    );

    form.register({
      name: 'emailOTP',
    });
  }, []);

  form.watch();

  return form;
};

const style = StyleSheet.create({
  container: {
    flex: 1,
  },
  otp: {
    alignItems: 'center',
  },
  codeField: {
    width: 288,
  },
  textTitle: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  spacer: {
    marginBottom: 24,
  },
  button: {
    maxWidth: 312,
    alignSelf: 'center',
    marginVertical: 8,
  },
  dialog: {
    width: '100%',
    maxWidth: 424,
    minHeight: 548,
    marginTop: 64,
  },
});

export default EditUserPhoneUnauthenticated;
